import React from 'react';
import styled from 'styled-components';
import { Type } from '@spotify-internal/encore-creator-web';
import {
  azure,
  spacer16,
  spacer32,
  spacer48,
  screenMdMin,
} from '@spotify-internal/encore-foundation';
import { pageConstraintStyles, PAGE_GUTTER } from './styles';

export function Features() {
  return (
    <FeaturesGrid>
      <FeatureImage
        src="/images/landing_page/feature-1.png"
        alt=""
        style={{ gridArea: 'feature-img-1' }}
      />
      <div style={{ gridArea: 'feature-text-1' }}>
        <FeatureHeader>Be where millions of listeners&nbsp;are</FeatureHeader>
        <Type as="p" variant={Type.body1}>
          Spotify has over 250 million listeners across more than 75 countries worldwide just
          waiting to discover podcasts like yours. We're the second most popular place to listen to
          podcasts in the world&nbsp;— and growing fast. Our podcast audience has nearly doubled
          since the start of 2019.
        </Type>
        <Type as="p" variant={Type.body1}>
          On Spotify, people can stream or download your show on Android and iOS, desktop, across
          gaming consoles, cars, TVs, smart speakers, and on whatever else they use to tune in
          <AzureLink
            href="https://spotify-everywhere.com/pages/product-categories"
            target="_blank"
            rel="noopener noreferrer"
          >
            *
          </AzureLink>
          .
        </Type>
      </div>
      <div style={{ gridArea: 'feature-text-2' }}>
        <FeatureHeader>Get to know your audience</FeatureHeader>
        <Type as="p" variant={Type.body1}>
          Because people are logged in and listening every day, Spotify gives you a powerful
          snapshot of who your listeners are, what episodes they're streaming the most, and even the
          music they love&nbsp;— insights that will help you plan future episodes or get sponsors.
        </Type>
        <Type as="p" variant={Type.body1}>
          You can also dive deeper into each episode with details around how long people listen and
          where they start and stop, all while tracking your follower growth.
        </Type>
      </div>
      <FeatureImage
        src="/images/landing_page/feature-2.png"
        alt=""
        style={{ gridArea: 'feature-img-2' }}
      />
      <FeatureImage
        src="/images/landing_page/feature-3.png"
        alt=""
        style={{ gridArea: 'feature-img-3' }}
      />
      <div style={{ alignSelf: 'end', marginBottom: '44px', gridArea: 'feature-text-3' }}>
        <FeatureHeader>We’re here for creators</FeatureHeader>
        <Type as="p" variant={Type.body1}>
          Spotify was designed to help artists get discovered&nbsp;— now we’re working to do the
          same for podcasters. Audio is what we do.
        </Type>
        <Type as="p" variant={Type.body1}>
          With editorial and personalized playlists (like Your Daily Podcasts, This Week's Top
          Podcasts, and Brain Snacks), we're focused on developing powerful new ways to help you
          grow your audience.
        </Type>
      </div>
    </FeaturesGrid>
  );
}

const FeaturesGrid = styled.div`
  display: grid;
  grid-gap: ${spacer32};
  grid-template-areas:
    'feature-img-1'
    'feature-text-1'
    'feature-img-2'
    'feature-text-2'
    'feature-img-3'
    'feature-text-3';
  grid-template-columns: 1fr;
  justify-items: center;
  margin: 40px 16px 0;

  @media (min-width: ${screenMdMin}) {
    ${pageConstraintStyles}

    align-items: center;
    grid-gap: ${spacer16};
    grid-template-areas:
      'feature-img-1 feature-text-1'
      'feature-text-2 feature-img-2'
      'feature-img-3 feature-text-3';
    grid-template-columns: 1fr 1fr;
    padding-bottom: 312px;

    div:nth-child(odd) {
      margin-left: ${PAGE_GUTTER};
    }
    div:nth-child(even) {
      margin-right: ${PAGE_GUTTER};
    }
  }
`;

const FeatureHeader = styled(Type).attrs({
  forwardedAs: 'h2',
  variant: Type.heading2,
})`
  font-size: 28px;
  line-height: 30px;

  @media (min-width: ${screenMdMin}) {
    font-size: 54px;
    line-height: 54px;
  }
`;

const FeatureImage = styled.img`
  max-width: 585px;
  width: 100%;

  @media (min-width: ${screenMdMin}) {
    &:nth-child(odd) {
      margin-left: ${spacer48};
    }
  }
`;

const AzureLink = styled.a`
  color: ${azure};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;
